import {
  Button,
  Form,
  FormItem,
  Input,
  PasswordInput,
  useForm,
} from "@/frontend/components/common";
import { authService, poolInvitationService } from "@/frontend/services";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type { RegisterRequestDTO } from "@/shared/dto";
import { useEffect, useState } from "react";
import { message } from "antd";

export function RegisterForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = useForm();
  
  const invitationToken = searchParams.get("token");
  const prefilledEmail = searchParams.get("email");
  const [hasInvitation, setHasInvitation] = useState(false);

  useEffect(() => {
    if (invitationToken && prefilledEmail) {
      setHasInvitation(true);
      form.setFieldsValue({ email: prefilledEmail });
    }
  }, [invitationToken, prefilledEmail, form]);

  const handleSubmit = async (values: RegisterFormValues) => {
    try {
      const registerData: RegisterRequestDTO = {
        email: values.email,
        password: values.password,
        username: values.username,
        firstName: values.firstName,
        lastName: values.lastName,
        secondName: values.secondName || undefined,
        secondLastName: values.secondLastName || undefined,
        phone: values.phone || undefined,
        dni: values.dni || undefined,
      };

      const response = await authService.register(registerData);

      if (response.success && response.data) {
        if (invitationToken) {
          message.success("¡Cuenta creada! Inicia sesión para unirte a la polla.");
          navigate(`/login?token=${invitationToken}`, {
            state: { message: "¡Cuenta creada exitosamente! Inicia sesión para continuar." },
          });
        } else {
          navigate("/login", {
            state: { message: "¡Cuenta creada exitosamente!" },
          });
        }
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Error al registrar usuario";
      form.setFields([{ name: "email", errors: [errorMessage] }]);
    }
  };
  return (
    <Form
      form={form}
      onFinish={handleSubmit}
      layout="vertical"
      className="space-y-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormItem
          name="username"
          label={<span className="text-slate-700 text-sm font-semibold ml-1">Nombre de Usuario</span>}
          rules={[{ required: true, message: "Campo requerido" }]}
        >
          <Input
            placeholder="ej. futbol_fan"
            size="large"
            className="bg-slate-50 border-slate-200 text-slate-900 h-14 rounded-xl focus:border-emerald-500 focus:bg-white transition-all placeholder:text-slate-400"
          />
        </FormItem>

        <FormItem
          name="email"
          label={<span className="text-slate-700 text-sm font-semibold ml-1">Correo Electrónico</span>}
          rules={[
            { required: true, message: "Campo requerido" },
            { type: "email", message: "Formato inválido" },
          ]}
        >
          <Input
            placeholder="correo@ejemplo.com"
            size="large"
            disabled={hasInvitation}
            className={`bg-slate-50 border-slate-200 text-slate-900 h-14 rounded-xl focus:border-emerald-500 focus:bg-white transition-all placeholder:text-slate-400 ${hasInvitation ? "bg-slate-100 cursor-not-allowed" : ""}`}
          />
        </FormItem>

        <FormItem
          name="firstName"
          label={<span className="text-slate-700 text-sm font-semibold ml-1">Nombres</span>}
          rules={[{ required: true, message: "Campo requerido" }]}
        >
          <Input
            placeholder="Tus nombres"
            size="large"
            className="bg-slate-50 border-slate-200 text-slate-900 h-14 rounded-xl focus:border-emerald-500 focus:bg-white transition-all placeholder:text-slate-400"
          />
        </FormItem>

        <FormItem
          name="lastName"
          label={<span className="text-slate-700 text-sm font-semibold ml-1">Apellidos</span>}
          rules={[{ required: true, message: "Campo requerido" }]}
        >
          <Input
            placeholder="Tus apellidos"
            size="large"
            className="bg-slate-50 border-slate-200 text-slate-900 h-14 rounded-xl focus:border-emerald-500 focus:bg-white transition-all placeholder:text-slate-400"
          />
        </FormItem>
      </div>

      <FormItem
        name="password"
        label={<span className="text-slate-700 text-sm font-semibold ml-1">Contraseña Segura</span>}
        rules={[
          { required: true, message: "Campo requerido" },
          { min: 8, message: "Mínimo 8 caracteres" },
          {
            validator: (_, value) => {
              if (!value) return Promise.resolve();
              const hasNumber = /[0-9]/.test(value);
              const hasLetter = /[a-zA-Z]/.test(value);
              const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/.test(value);
              if (!hasNumber || !hasLetter || !hasSpecial) {
                return Promise.reject("Combina letras, números y símbolos");
              }
              return Promise.resolve();
            },
          }
        ]}
      >
        <PasswordInput
          placeholder="••••••••"
          size="large"
          className="bg-slate-50 border-slate-200 text-slate-900 h-14 rounded-xl focus:border-emerald-500 focus:bg-white transition-all"
        />
      </FormItem>

      <div className="pt-8 flex flex-col gap-6">
        <Button
          type="primary"
          htmlType="submit"
          size="large"
          block
          className="h-16 bg-emerald-600 hover:bg-emerald-500 border-none rounded-xl text-lg font-bold shadow-lg shadow-emerald-100 text-white transition-all transform active:scale-[0.98]"
        >
          Crear cuenta ahora
        </Button>

        <div className="text-center">
          <p className="text-slate-500 text-sm font-medium">
            ¿Ya tienes una cuenta?{" "}
            <Link 
              to="/login"
              className="text-emerald-600 font-bold hover:text-emerald-700 transition-colors ml-1"
            >
              Inicia sesión aquí
            </Link>
          </p>
        </div>
      </div>
    </Form>
  );
}

interface RegisterFormValues {
  email: string;
  password: string;
  username: string;
  firstName: string;
  lastName: string;
  secondName?: string;
  secondLastName?: string;
  phone?: string;
  dni?: string;
}
